import React, { useEffect, useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { apiCall } from '../utils/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface Gig {
  id: number;
  name: string;
  venue: string | null;
  status: string;
  started_at: string;
}

const FlyerThird: React.FC<{ gig: Gig | null; catalogueUrl: string }> = ({ gig, catalogueUrl }) => (
  <div className="flyer-third" style={{
    fontFamily: 'Georgia, serif',
    background: '#f5f5dc',
    padding: '12px 30px',
    boxSizing: 'border-box',
  }}>
    {/* QR Code + All info side by side */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'center' }}>
      <div style={{
        padding: '8px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        flexShrink: 0,
      }}>
        <QRCodeSVG
          value={catalogueUrl}
          size={120}
          level="H"
          includeMargin={true}
          style={{ display: 'block' }}
        />
      </div>

      <div style={{ textAlign: 'left' }}>
        <h1 style={{
          fontSize: '1.6rem',
          margin: '0 0 2px',
          color: '#8b4513',
        }}>
          RODEOKE
        </h1>
        <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 6px', fontStyle: 'italic' }}>
          Country Karaoke with a Live Band
        </p>
        {gig && (
          <p style={{ margin: '0 0 6px', color: '#8b4513', fontSize: '0.9rem', fontWeight: 'bold' }}>
            {gig.name}{gig.venue ? ` — ${gig.venue}` : ''}
          </p>
        )}
        <p style={{ fontSize: '1rem', color: '#333', fontWeight: 'bold', margin: '0 0 4px' }}>
          Scan to Pick Your Song!
        </p>
        <div style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>
          <span><strong>1.</strong> Scan QR &nbsp; <strong>2.</strong> Browse songs &nbsp; <strong>3.</strong> Join the queue &nbsp; <strong>4.</strong> Sing!</span>
        </div>
      </div>
    </div>
  </div>
);

const QRFlyerPage: React.FC = () => {
  const [gig, setGig] = useState<Gig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const flyerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchGig = async () => {
      try {
        const res = await apiCall('/api/songs/gigs/current');
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setGig(data);
          } else {
            setError('No active gig. Start a gig first to generate the QR flyer.');
          }
        } else {
          setError('Failed to check gig status');
        }
      } catch (err) {
        setError('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };
    fetchGig();
  }, []);

  const catalogueUrl = `${window.location.origin}/gig/catalogue`;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!flyerRef.current) return;

    setGenerating(true);
    try {
      const canvas = await html2canvas(flyerRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f5f5dc'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 210;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      const gigName = gig?.name?.replace(/[^a-z0-9]/gi, '-') || 'rodeoke';
      pdf.save(`${gigName}-flyer.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF. Please try printing instead.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: '#1a0f0a', minHeight: '100vh', color: '#f5deb3' }}>
        <p>Checking gig status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: '#1a0f0a', minHeight: '100vh', color: '#f5deb3' }}>
        <h2 style={{ color: '#e74c3c' }}>Cannot Generate Flyer</h2>
        <p>{error}</p>
        <a href="/gig/queue" style={{ color: '#ffd700' }}>Go to Queue Admin to start a gig</a>
      </div>
    );
  }

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          html, body {
            margin: 0;
            padding: 0;
            height: auto;
            overflow: visible;
          }
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto;
            box-sizing: border-box;
            padding: 0 !important;
          }
          .flyer-third {
            height: calc((100vh - 0.8in) / 3);
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .cut-line {
            border-top: 2px dashed #ccc !important;
          }
          .no-print {
            display: none !important;
          }
          .print-outer {
            min-height: 0 !important;
            height: auto !important;
            background: none !important;
            overflow: visible !important;
          }
          @page {
            margin: 0.4in;
            size: A4 portrait;
          }
        }
      `}</style>

      <div className="print-outer" style={{ background: '#1a0f0a', minHeight: '100vh' }}>
        {/* Control buttons - hidden when printing */}
        <div className="no-print" style={{
          padding: '20px',
          textAlign: 'center',
          borderBottom: '1px solid #5a3a1a',
          background: '#28190f'
        }}>
          <button
            onClick={handleDownloadPDF}
            disabled={generating}
            style={{
              padding: '12px 32px',
              fontSize: '18px',
              background: generating ? '#5a3a1a' : '#c8850f',
              color: generating ? '#a08060' : '#1a0f0a',
              border: '1px solid #5a3a1a',
              borderRadius: '6px',
              cursor: generating ? 'not-allowed' : 'pointer',
              marginRight: '12px',
              fontWeight: 600
            }}
          >
            {generating ? 'Generating...' : 'Download PDF'}
          </button>
          <button
            onClick={handlePrint}
            style={{
              padding: '12px 32px',
              fontSize: '18px',
              background: '#8b4513',
              color: '#f5deb3',
              border: '1px solid #5a3a1a',
              borderRadius: '6px',
              cursor: 'pointer',
              marginRight: '12px',
              fontWeight: 600
            }}
          >
            Print Directly
          </button>
          <a
            href="/gig/queue"
            style={{
              display: 'inline-block',
              padding: '12px 32px',
              fontSize: '18px',
              background: '#3a2415',
              color: '#f5deb3',
              textDecoration: 'none',
              borderRadius: '6px',
              border: '1px solid #5a3a1a'
            }}
          >
            Back to Admin
          </a>
        </div>

        {/* Printable area — three per page */}
        <div ref={flyerRef} className="print-area" style={{
          maxWidth: '8.5in',
          margin: '0 auto',
          background: '#f5f5dc',
        }}>
          <FlyerThird gig={gig} catalogueUrl={catalogueUrl} />
          <div className="cut-line" style={{ borderTop: '2px dashed #ccc', margin: '0 30px' }} />
          <FlyerThird gig={gig} catalogueUrl={catalogueUrl} />
          <div className="cut-line" style={{ borderTop: '2px dashed #ccc', margin: '0 30px' }} />
          <FlyerThird gig={gig} catalogueUrl={catalogueUrl} />
        </div>
      </div>
    </>
  );
};

export default QRFlyerPage;
